import { OpenPosition } from '@flipster/r230522/original/position';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber } from '@flipster/utils/num';

// https://github.prex.sh/Prex/prex-api-doc/blob/develop/Variables%20and%20Functions.md#isolated_margin_assigned
// isolated_margin_assigned = isolatedMarginInitial + isolatedMarginAdded - isolatedMarginRealizedFee
export const calcIsolatedMarginAssigned = (
  isolatedMarginInitial: string,
  isolatedMarginAdded: string | undefined,
  isolatedMarginRealizedFee: string,
) =>
  Num(isolatedMarginInitial)
    .plus(isolatedMarginAdded || '0')
    .minus(isolatedMarginRealizedFee)
    .toString();

export const calcPositionMargin = (position: OpenPosition) => {
  switch (position.marginType) {
    case 'Cross':
      return position.initPositionMargin;
    case 'Isolated':
      return calcIsolatedMarginAssigned(
        position.isolatedMarginInitial,
        position.isolatedMarginAdded,
        position.isolatedMarginRealizedFee,
      );
    default:
      // oxlint-disable-next-line no-unused-expressions
      position.marginType satisfies never;
      // oxlint-disable-next-line no-console
      console.error(`Unexpected margin type: ${position.marginType}`);
      return '0';
  }
};

export const calcPositionMarginExcludingFee = (position: OpenPosition) => {
  switch (position.marginType) {
    case 'Cross':
      return position.initPositionMargin;
    case 'Isolated':
      return Num(position.isolatedMarginInitial)
        .plus(position.isolatedMarginAdded || 0)
        .toString();
    default:
      // oxlint-disable-next-line no-unused-expressions
      position.marginType satisfies never;
      // oxlint-disable-next-line no-console
      console.error(`Unexpected margin type: ${position.marginType}`);
      return '0';
  }
};

// https://github.prex.sh/Prex/prex-api-doc/blob/develop/Variables%20and%20Functions.md#new_liquidation_price
// new_liquidation_price = (isolatedMarginAssigned + additionalMargin - averageEntryPrice * position) / (maintMarginRate * abs(position) - position)
export const calcNewLiquidationPrice = (
  valueType: 'add' | 'remove',
  isolatedMarginAssigned: string, // isolatedMarginInitial + isolatedMarginAdded - isolatedMarginRealizedFee
  amount: string, // how much margin user trying to add for given position, same as user_input for Add Funds, (-1) * user_input for Remove Funds
  averageEntryPrice: string,
  position: string,
  maintMarginRate: string,
  precision: number,
) => {
  const additionalMargin = valueType === 'add' ? amount : Num(amount).multipliedBy(-1);
  const numerator = Num(isolatedMarginAssigned)
    .plus(additionalMargin)
    .minus(Num(averageEntryPrice).multipliedBy(position));
  const denominator = Num(maintMarginRate).multipliedBy(Num(position).abs()).minus(position);
  const result = numerator.dividedBy(denominator);

  if (result.isLessThan(0)) return undefined;

  return result.toFixed(precision).toString();
};

// https://github.prex.sh/Prex/prex-api-doc/blob/develop/Variables%20and%20Functions.md#isolated_margin_removable
// isolated_margin_removable = max(0, isolatedMarginAdded + min(-isolatedMarginRealizedFee + unrealizedPnl, 0))
export const calcIsolatedMarginRemovable = (
  valueType: 'add' | 'remove',
  availableFunds: string,
  isolatedMarginAdded: string | undefined,
  isolatedMarginRealizedFee: string,
  unrealizedPnl: string,
) => {
  if (valueType === 'add') return Num(availableFunds).toFixed(UsdtPrecision).toString();

  return BigNumber.max(
    0,
    Num(isolatedMarginAdded || '0').plus(BigNumber.min(0, Num(unrealizedPnl).minus(isolatedMarginRealizedFee))),
  )
    .toFixed(UsdtPrecision)
    .toString();
};
